/**
 * Global Game State Store (Zustand)
 *
 * Centralized state management for BDUDS game:
 * - Session and player management
 * - Game phase tracking (setup, waiting, playing, ended)
 * - Turn management
 * - Piece placement state
 * - Attack history
 * - Win condition state
 * - State persistence to localStorage
 *
 * This store integrates with the existing game logic hooks:
 * - usePiecePlacement (Phase 4.1)
 * - useAttack (Phase 4.2)
 * - useWinCondition (Phase 4.3)
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Game phases:
 * - setup: Players placing pieces
 * - waiting: One player ready, waiting for opponent
 * - playing: Both players ready, game in progress
 * - ended: Game over, winner determined
 */
const GAME_PHASES = {
  SETUP: 'setup',
  WAITING: 'waiting',
  PLAYING: 'playing',
  ENDED: 'ended',
};

/**
 * Initial state for the game store
 */
const initialState = {
  // Session management
  sessionId: null, // Active session (from URL, not persisted)
  persistedSessionId: null, // Last session ID (persisted for comparison)
  playerId: null,

  // Player state
  player1: null, // { id, ready }
  player2: null, // { id, ready }

  // Game state
  gamePhase: GAME_PHASES.SETUP,
  currentTurn: null, // player ID whose turn it is
  winner: null, // player ID of winner

  // Piece placement
  myPlacedPieces: [], // Array of placed piece objects
  opponentPlacedPieces: [], // Array of opponent's pieces (revealed after game)

  // Attack state
  myAttacks: [], // Array of my attack objects
  opponentAttacks: [], // Array of opponent's attack objects

  // Prize state
  myPrizes: [], // Array of my prize strings (up to 3)
  opponentPrizes: [], // Array of opponent's prizes (for transparency)

  // Prize wheel state
  showPrizeWheel: false, // Whether to show prize wheel
  prizeWheelSpinning: false, // Whether wheel is currently spinning
  selectedPrize: null, // The prize that was selected (null until selected)

  // Win condition state
  hasCapturedBonusPiece: false, // Whether winner captured the bonus piece

  // Bonus piece capture tracking (Phase 7.9.4.1)
  player1HasCapturedBonus: false, // Whether player 1 captured the bonus piece
  player2HasCapturedBonus: false, // Whether player 2 captured the bonus piece

  // Piece destroyed popup state (Phase 7.9.3.1)
  pieceDestroyedPopup: null, // { pieceType, playerWhoLost, piecesLostCount, totalPiecesCount } or null

  // Bonus piece captured popup state (Phase 7.9.3.2)
  bonusCapturedPopup: null, // { attackerPlayer } or null

  // Destroyed pieces visualization state (Phase 7.9.3.3)
  myDestroyedPieces: [], // Array of { type, coordinates } - pieces I lost
  opponentDestroyedPieces: [], // Array of { type, coordinates } - pieces opponent lost
};

/**
 * Create the Zustand store with persistence
 */
export const useGameStore = create(
  persist(
    (set, get) => ({
      ...initialState,
      _hasHydrated: false,

      // ========================================
      // Session Management
      // ========================================

      setSessionId: (sessionId) =>
        set({ sessionId, persistedSessionId: sessionId }),

      setPlayerId: (playerId) => set({ playerId }),

      isPlayer1: () => {
        const state = get();
        return state.player1?.id === state.playerId;
      },

      // ========================================
      // Player State Management
      // ========================================

      setPlayer1: (player) => set({ player1: player }),

      setPlayer2: (player) => set({ player2: player }),

      setPlayerReady: (playerId, ready) => {
        const state = get();
        if (state.player1?.id === playerId) {
          set({ player1: { ...state.player1, ready } });
        } else if (state.player2?.id === playerId) {
          set({ player2: { ...state.player2, ready } });
        }
      },

      areBothPlayersReady: () => {
        const state = get();
        return state.player1?.ready === true && state.player2?.ready === true;
      },

      // ========================================
      // Game Phase Management
      // ========================================

      setGamePhase: (phase) => set({ gamePhase: phase }),

      // ========================================
      // Turn Management
      // ========================================

      setCurrentTurn: (playerId) => set({ currentTurn: playerId }),

      isMyTurn: () => {
        const state = get();
        return state.currentTurn === state.playerId;
      },

      switchTurn: () => {
        const state = get();
        const { player1, player2, currentTurn } = state;

        if (!player1 || !player2) {
          return; // Can't switch turn if players not set
        }

        const nextTurn = currentTurn === player1.id ? player2.id : player1.id;
        set({ currentTurn: nextTurn });
      },

      // ========================================
      // Piece Placement
      // ========================================

      addPlacedPiece: (piece) => {
        const state = get();
        set({ myPlacedPieces: [...state.myPlacedPieces, piece] });
      },

      removePlacedPiece: (pieceIndex) => {
        const state = get();
        const newPieces = state.myPlacedPieces.filter(
          (_, index) => index !== pieceIndex
        );
        set({ myPlacedPieces: newPieces });
      },

      setOpponentPlacedPieces: (pieces) => {
        set({ opponentPlacedPieces: pieces });
      },

      clearPlacedPieces: () => {
        set({ myPlacedPieces: [] });
      },

      // ========================================
      // Attack Management
      // ========================================

      addMyAttack: (attack) => {
        const state = get();
        set({ myAttacks: [...state.myAttacks, attack] });
      },

      addOpponentAttack: (attack) => {
        const state = get();
        set({ opponentAttacks: [...state.opponentAttacks, attack] });
      },

      setMyAttacks: (attacks) => {
        set({ myAttacks: attacks });
      },

      setOpponentAttacks: (attacks) => {
        set({ opponentAttacks: attacks });
      },

      // ========================================
      // Win Condition
      // ========================================

      setWinner: (playerId) => {
        set({ winner: playerId, gamePhase: GAME_PHASES.ENDED });
      },

      didIWin: () => {
        const state = get();
        return state.winner === state.playerId;
      },

      // ========================================
      // Computed Values
      // ========================================

      getMyHitCount: () => {
        const state = get();
        return state.myAttacks.filter((attack) => attack.result === 'hit')
          .length;
      },

      getOpponentHitCount: () => {
        const state = get();
        return state.opponentAttacks.filter((attack) => attack.result === 'hit')
          .length;
      },

      getMyProgress: () => {
        const hitCount = get().getMyHitCount();
        const totalSquares = 18; // Total squares across all 5 pieces
        return Math.round((hitCount / totalSquares) * 100);
      },

      getOpponentProgress: () => {
        const hitCount = get().getOpponentHitCount();
        const totalSquares = 18; // Total squares across all 5 pieces
        return Math.round((hitCount / totalSquares) * 100);
      },

      // ========================================
      // Prize Management
      // ========================================

      setMyPrizes: (prizes) => set({ myPrizes: prizes }),

      setOpponentPrizes: (prizes) => set({ opponentPrizes: prizes }),

      canSubmitPrizes: () => {
        const state = get();
        // Can submit prizes if in setup phase and not ready yet
        const isPlayer1 = state.player1?.id === state.playerId;
        const playerReady = isPlayer1
          ? state.player1?.ready
          : state.player2?.ready;
        return state.gamePhase === 'setup' && !playerReady;
      },

      hasPrizes: () => {
        const state = get();
        return state.myPrizes.length > 0;
      },

      // ========================================
      // Prize Wheel Management
      // ========================================

      startPrizeWheelSpin: () => {
        set({
          showPrizeWheel: true,
          prizeWheelSpinning: true,
          selectedPrize: null,
        });
      },

      completePrizeWheelSpin: (prize) => {
        set({ prizeWheelSpinning: false, selectedPrize: prize });
      },

      closePrizeWheel: () => {
        set({ showPrizeWheel: false, prizeWheelSpinning: false });
      },

      // ========================================
      // Piece Destroyed Popup Management (Phase 7.9.3.1)
      // ========================================

      showPieceDestroyedPopup: (popupData) => {
        set({ pieceDestroyedPopup: popupData });
      },

      dismissPieceDestroyedPopup: () => {
        set({ pieceDestroyedPopup: null });
      },

      // Phase 7.9.3.2: Bonus piece captured popup
      showBonusCapturedPopup: (popupData) => {
        set({ bonusCapturedPopup: popupData });
      },

      dismissBonusCapturedPopup: () => {
        set({ bonusCapturedPopup: null });
      },

      // ========================================
      // WebSocket Message Handlers
      // ========================================

      handleWebSocketMessage: (message) => {
        const state = get();

        switch (message.type) {
          case 'CONNECTED':
            // Initialize player slots from game state
            const player1Data = message.gameState?.players?.player1
              ? {
                  id: 'player1',
                  ready: message.gameState.players.player1.ready || false,
                }
              : null;

            const player2Data = message.gameState?.players?.player2
              ? {
                  id: 'player2',
                  ready: message.gameState.players.player2.ready || false,
                }
              : null;

            set({
              playerId: message.player,
              gamePhase: message.gameState?.gamePhase || state.gamePhase,
              currentTurn: message.gameState?.currentTurn || state.currentTurn,
              player1: player1Data,
              player2: player2Data,
            });
            break;

          case 'PLAYER_JOINED':
            // Another player joined the session - initialize their slot
            if (message.player === 'player1' && !state.player1) {
              set({ player1: { id: 'player1', ready: false } });
            } else if (message.player === 'player2' && !state.player2) {
              set({ player2: { id: 'player2', ready: false } });
            }
            break;

          case 'PIECE_PLACED':
            // Opponent placed a piece (we don't get coordinates for security)
            // Just track that they placed something
            break;

          case 'PLAYER_READY':
            if (message.player !== state.playerId) {
              // Opponent is ready - update their ready status but NOT our gamePhase
              // Our gamePhase should only change when WE click ready
              const isPlayer1 = state.player1?.id === message.player;
              const isPlayer2 = state.player2?.id === message.player;

              if (isPlayer1 && state.player1) {
                set({ player1: { ...state.player1, ready: true } });
              } else if (isPlayer2 && state.player2) {
                set({ player2: { ...state.player2, ready: true } });
              }
              // Don't set gamePhase here - each player transitions to 'waiting'
              // only when they complete their own setup
            }
            break;

          case 'GAME_START':
            // Extract opponent prizes from game state
            const opponentPlayerKey =
              state.playerId === 'player1' ? 'player2' : 'player1';
            const opponentPrizesFromGameStart =
              message.gameState?.players?.[opponentPlayerKey]?.prizes || [];

            set({
              gamePhase: 'playing',
              currentTurn: message.firstTurn,
              opponentPrizes: opponentPrizesFromGameStart,
            });
            break;

          case 'ATTACK_RESULT':
            if (message.player !== state.playerId) {
              // Opponent attacked
              const attack = {
                coordinate: message.coordinate,
                result: message.isHit ? 'hit' : 'miss',
                pieceType: message.pieceType,
                pieceDestroyed: message.pieceDestroyed,
                timestamp: Date.now(),
              };

              set({
                opponentAttacks: [...state.opponentAttacks, attack],
                currentTurn: message.nextTurn,
              });
            } else {
              // My attack result confirmed - add to myAttacks
              const attack = {
                coordinate: message.coordinate,
                result: message.isHit ? 'hit' : 'miss',
                pieceType: message.pieceType,
                pieceDestroyed: message.pieceDestroyed,
                timestamp: Date.now(),
              };

              set({
                myAttacks: [...state.myAttacks, attack],
                currentTurn: message.nextTurn,
              });
            }
            break;

          case 'GAME_END':
            console.log('[gameStore] Received GAME_END', {
              winner: message.winner,
              hasCapturedBonusPiece: message.hasCapturedBonusPiece,
              currentGamePhase: state.gamePhase,
            });
            set({
              gamePhase: 'ended',
              winner: message.winner,
              hasCapturedBonusPiece: message.hasCapturedBonusPiece || false,
              // Dismiss piece destroyed and bonus captured popups when game ends (so we go straight to victory modal)
              pieceDestroyedPopup: null,
              bonusCapturedPopup: null,
            });
            console.log('[gameStore] Updated state after GAME_END', {
              gamePhase: get().gamePhase,
              winner: get().winner,
              hasCapturedBonusPiece: get().hasCapturedBonusPiece,
            });
            break;

          case 'ERROR':
            // Error handling - could store in state or just log
            console.error('WebSocket error:', message.message);
            break;

          case 'PONG':
            // Heartbeat response
            break;

          case 'SUCCESS':
            // Handle successful SET_READY action
            if (message.action === 'SET_READY') {
              // Mark current player as ready
              if (state.playerId === state.player1?.id) {
                set({
                  player1: { ...state.player1, ready: true },
                  gamePhase: 'waiting', // Transition to waiting phase
                });
              } else if (state.playerId === state.player2?.id) {
                set({
                  player2: { ...state.player2, ready: true },
                  gamePhase: 'waiting',
                });
              }
            }
            // Handle successful SUBMIT_PRIZES action
            if (message.action === 'SUBMIT_PRIZES') {
              // Prizes already set locally, just acknowledge success
              console.log('[gameStore] Prizes submitted successfully');
            }
            break;

          case 'PRIZES_SUBMITTED':
            // Prizes are no longer broadcast during setup
            // They will be revealed when the game starts via GAME_START message
            break;

          case 'PRIZE_SELECTED':
            // Prize wheel result received
            console.log('[gameStore] Prize selected:', {
              winner: message.winner,
              selectedPrize: message.selectedPrize,
            });
            set({
              selectedPrize: message.selectedPrize,
              prizeWheelSpinning: false,
            });
            break;

          case 'PRIZE_SPIN_STARTED':
            // Prize wheel spin started by winner
            console.log('[gameStore] Prize spin started by winner:', {
              winner: message.winner,
            });
            // This message is received by both players, so both will start spinning
            set({
              showPrizeWheel: true,
              prizeWheelSpinning: true,
              selectedPrize: null,
            });
            break;

          case 'PIECE_DESTROYED':
            // Phase 7.9.2.1: Piece destruction notification
            console.log('[gameStore] Piece destroyed:', {
              player: message.player,
              pieceType: message.pieceType,
              attacker: message.attacker,
              coordinate: message.coordinate,
              nextTurn: message.nextTurn,
            });

            // Phase 7.9.3.1: Calculate pieces lost count and show popup
            // Count how many pieces have been destroyed by looking at attacks
            const playerWhoLost = message.player;
            const isMyPieceLost = playerWhoLost === state.playerId;

            // Count unique pieces destroyed by checking pieceDestroyed flags in attacks
            const attacksAgainstLoser = isMyPieceLost
              ? state.opponentAttacks
              : state.myAttacks;

            const piecesDestroyedCount = attacksAgainstLoser.filter(
              (attack) => attack.pieceDestroyed === true
            ).length;

            // Determine total pieces count (4 regular + 1 bonus if present)
            // We can infer this from game state or assume 5 for now
            const totalPiecesCount = 5;

            // Phase 7.9.3.2: Check if the destroyed piece is the bonus piece
            const isBonusPiece =
              message.pieceType === 'bonus' || message.pieceType === 'Bonus';

            if (isBonusPiece) {
              // Phase 7.9.4.1: Set hasCapturedBonus flag for the attacker
              const attackerId = message.attacker;
              if (attackerId === state.player1?.id) {
                set({ player1HasCapturedBonus: true });
              } else if (attackerId === state.player2?.id) {
                set({ player2HasCapturedBonus: true });
              }

              // Show special bonus piece captured popup
              get().showBonusCapturedPopup({
                attackerPlayer: message.attacker,
              });
            } else {
              // Show regular piece destroyed popup
              get().showPieceDestroyedPopup({
                pieceType: message.pieceType,
                playerWhoLost: message.player,
                piecesLostCount: piecesDestroyedCount,
                totalPiecesCount: totalPiecesCount,
              });
            }

            // Phase 7.9.3.3: Store destroyed piece for visualization
            const destroyedPieceData = {
              type: message.pieceType,
              coordinates: message.coordinates || [],
            };

            if (isMyPieceLost) {
              // I lost this piece
              set({
                currentTurn: message.nextTurn,
                myDestroyedPieces: [
                  ...state.myDestroyedPieces,
                  destroyedPieceData,
                ],
              });
            } else {
              // Opponent lost this piece (show on their grid)
              set({
                currentTurn: message.nextTurn,
                opponentDestroyedPieces: [
                  ...state.opponentDestroyedPieces,
                  destroyedPieceData,
                ],
              });
            }
            break;

          default:
            console.warn('Unknown message type:', message.type);
        }
      },

      // ========================================
      // Enhanced Selectors
      // ========================================

      canPlacePiece: () => {
        const state = get();
        // Can only place pieces in setup phase and if not ready
        return (
          state.gamePhase === 'setup' &&
          state.myPlacedPieces.length < 5 &&
          (state.player1?.id === state.playerId
            ? !state.player1?.ready
            : !state.player2?.ready)
        );
      },

      canSetReady: () => {
        const state = get();
        // Can only set ready if in setup phase, all 5 pieces placed, and at least 1 prize submitted
        return (
          state.gamePhase === 'setup' &&
          state.myPlacedPieces.length === 5 &&
          state.myPrizes.length > 0 &&
          (state.player1?.id === state.playerId
            ? !state.player1?.ready
            : !state.player2?.ready)
        );
      },

      canMakeAttack: () => {
        const state = get();
        // Can only attack if in playing phase and it's my turn
        return (
          state.gamePhase === 'playing' && state.currentTurn === state.playerId
        );
      },

      getAttackAtCoordinate: (coordinate) => {
        const state = get();
        const normalizedCoord = coordinate.toUpperCase();
        return (
          state.myAttacks.find(
            (attack) => attack.coordinate.toUpperCase() === normalizedCoord
          ) || null
        );
      },

      hasAttackedCoordinate: (coordinate) => {
        return get().getAttackAtCoordinate(coordinate) !== null;
      },

      // ========================================
      // State Reset
      // ========================================

      reset: () => {
        set(initialState);
      },

      resetGame: () => {
        const state = get();
        set({
          ...initialState,
          sessionId: state.sessionId, // Preserve session
          playerId: state.playerId, // Preserve player ID
          player1: state.player1 ? { ...state.player1, ready: false } : null,
          player2: state.player2 ? { ...state.player2, ready: false } : null,
        });
      },
    }),
    {
      name: 'bduds-game-state', // localStorage key
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist necessary state (exclude computed values and internal flags)
        // NOTE: sessionId is NOT persisted - URL is the source of truth
        // persistedSessionId IS persisted to detect session changes
        persistedSessionId: state.persistedSessionId,
        playerId: state.playerId,
        player1: state.player1,
        player2: state.player2,
        gamePhase: state.gamePhase,
        currentTurn: state.currentTurn,
        winner: state.winner,
        myPlacedPieces: state.myPlacedPieces,
        opponentPlacedPieces: state.opponentPlacedPieces,
        myAttacks: state.myAttacks,
        opponentAttacks: state.opponentAttacks,
        myPrizes: state.myPrizes,
        opponentPrizes: state.opponentPrizes,
        player1HasCapturedBonus: state.player1HasCapturedBonus,
        player2HasCapturedBonus: state.player2HasCapturedBonus,
        myDestroyedPieces: state.myDestroyedPieces,
        opponentDestroyedPieces: state.opponentDestroyedPieces,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state._hasHydrated = true;
        }
      },
    }
  )
);

// Export game phase constants for use in components
export { GAME_PHASES };
